import { useEffect } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useCommunityGuidelinesQuery } from 'Generated/graphql-types'

const CROSS_RESILIA_DOMAIN = '.resilia.com'
const SHELL_RETURN_TO_COOKIE = 'resilia_shell_return_to'
// 30 min, deliberately short: the cookie exists to survive the Auth0 embed
// round-trip (seconds) plus a session-length embed visit — while present it
// suppresses the legacy→shell redirect on every path, and a longer TTL lets
// shell users silently keep working in legacy for a full day per hop (#6323).
const SHELL_RETURN_TO_TTL_SECONDS = 1800

export const setCommunityCookie = ({
  agreedCommunityGuidelines,
}: {
  agreedCommunityGuidelines: boolean
}) => {
  const isResiliaDomain = window.location.host.includes(CROSS_RESILIA_DOMAIN)
  const domain = isResiliaDomain ? `domain=${CROSS_RESILIA_DOMAIN};` : ''

  const cookieValue = JSON.stringify({
    agreedCommunityGuidelines,
  })

  document.cookie = `community_agreements=${cookieValue};${domain}max-age=31536000`
}

// Pass `null` to clear. Sets on `.resilia.com` so Circle's snippet (served from
// app.resilia.com but executed on community.resilia.com) can read it.
export const setShellReturnToCookie = (returnTo: string | null) => {
  const isResiliaDomain = window.location.host.includes(CROSS_RESILIA_DOMAIN)
  const domain = isResiliaDomain ? `domain=${CROSS_RESILIA_DOMAIN};` : ''

  if (returnTo) {
    const encoded = encodeURIComponent(returnTo)
    document.cookie = `${SHELL_RETURN_TO_COOKIE}=${encoded};${domain}path=/;max-age=${SHELL_RETURN_TO_TTL_SECONDS};SameSite=Lax`
  } else {
    document.cookie = `${SHELL_RETURN_TO_COOKIE}=;${domain}path=/;max-age=0`
  }
}

// True if the shell-handoff cookie is present (a non-empty value). Used by
// `useShellAppRedirect` to skip the legacy-to-shell redirect on `/home` when
// the user just came from shell — the cookie is the freshness proof that
// survives the Auth0 round-trip even after URL params are stripped.
export const hasShellReturnToCookie = (): boolean => {
  if (typeof document === 'undefined') return false
  return document.cookie
    .split(';')
    .map((c) => c.trim())
    .some(
      (c) =>
        c.startsWith(`${SHELL_RETURN_TO_COOKIE}=`) &&
        c.length > `${SHELL_RETURN_TO_COOKIE}=`.length
    )
}

export const useCommunityCookie = () => {
  const { isAuthenticated } = useAuth0()

  const { data, loading, error } = useCommunityGuidelinesQuery({
    fetchPolicy: 'no-cache',
    skip: !isAuthenticated,
  })

  const agreedCommunityGuidelines = loading
    ? undefined
    : Boolean(data?.communityGuidelines?.isAgreed)
  useEffect(() => {
    if (agreedCommunityGuidelines !== undefined) {
      setCommunityCookie({ agreedCommunityGuidelines })
    }
  }, [agreedCommunityGuidelines])

  return {
    loading,
    error,
  }
}
